import { formatSubmissionStatusFromRow } from "@/helpers/submissionStatus";

const FILESTACK_CDN = "https://cdn.filestackcontent.com/";

const SHAPE_PDF = {
  quote: {
    topKey: "quotePdf",
    nestedKey: "shapeQuotePackage",
    filename: "quote.pdf",
    label: "Quote",
  },
  policy: {
    topKey: "policyPdf",
    nestedKey: "shapePolicyPackage",
    filename: "policy.pdf",
    label: "Policy Form",
  },
};

/** Matches backend ``is_s3_file_ref`` (storage s3, or key path with ``/``). */
export function isS3PdfRef(doc) {
  if (!doc || typeof doc !== "object") return false;
  if ((doc.storage || "").toLowerCase() === "s3") return true;
  if (doc.s3_key) return true;
  const key = (doc.s3_key || doc.handle || "").trim();
  return key.includes("/");
}

function policyPdfsFromDetail(detail) {
  if (!detail || typeof detail !== "object") return [];
  const list = detail.policyPdfs || detail.policy_pdfs;
  if (Array.isArray(list)) return list;
  return list ? [list] : [];
}

function binderPdfsFromDetail(detail) {
  if (!detail || typeof detail !== "object") return [];
  const list = detail.binderPdfs || detail.binder_pdfs;
  if (Array.isArray(list)) return list;
  return list ? [list] : [];
}

function quotePdfsFromDetail(detail) {
  if (!detail || typeof detail !== "object") return [];
  const list = detail.quotePdfs || detail.quote_pdfs;
  if (Array.isArray(list)) return list;
  return list ? [list] : [];
}

/** Default PDF for a kind: policy = first; quote = last (latest broker attach). */
function shapePdfDocFromDetail(quote, topKey, nestedKey) {
  const detail = quote?.detail;
  if (!detail || typeof detail !== "object") return null;
  let doc;
  if (topKey === "policyPdf") {
    const list = policyPdfsFromDetail(detail);
    if (list.length) doc = list[0];
  } else if (topKey === "quotePdf") {
    const list = quotePdfsFromDetail(detail);
    if (list.length) doc = list.at(-1);
  }
  if (!doc) doc = detail[topKey] || detail[nestedKey]?.[topKey];
  return doc && typeof doc === "object" ? doc : null;
}

function shapePdfFromDetail(quote, topKey, nestedKey, filename) {
  const doc = shapePdfDocFromDetail(quote, topKey, nestedKey);
  return doc ? viewerPdf(doc, filename) : null;
}

/** @param {"quote"|"policy"} kind */
export function getShapePdf(quote, kind) {
  const cfg = SHAPE_PDF[kind];
  if (!cfg) return null;
  return shapePdfFromDetail(quote, cfg.topKey, cfg.nestedKey, cfg.filename);
}

/**
 * The stored file name of the document ``getShapePdf`` opens for a kind, or "" when the
 * record carries none (the viewer's own fallback name is not a real file name).
 * @param {"quote"|"policy"} kind
 */
export function shapePdfFileName(quote, kind) {
  const cfg = SHAPE_PDF[kind];
  if (!cfg) return "";
  const doc = shapePdfDocFromDetail(quote, cfg.topKey, cfg.nestedKey);
  return doc ? String(doc.filename || doc.file_name || "").trim() : "";
}

/**
 * Every storage key of the exact document ``getShapePdf`` would open (S2CWB-236).
 *
 * The analysis run records a document by ``s3_key`` or ``handle``; comparing against
 * both lets a citation prove it names THIS document before the viewer opens it.
 * @param {"quote"|"policy"} kind
 */
export function shapePdfKeys(quote, kind) {
  const cfg = SHAPE_PDF[kind];
  if (!cfg) return [];
  const doc = shapePdfDocFromDetail(quote, cfg.topKey, cfg.nestedKey);
  return doc ? docKeys(doc) : [];
}

/**
 * Keys a document ref is known by. Mirrors the BWB API's ``_ref_key``: a storage key
 * (``s3_key`` / ``handle`` / ``fileHandle``) when there is one, otherwise the URL
 * without its query string - so every ref the API sends for analysis can be matched.
 */
function docKeys(doc) {
  const keys = [doc.handle, doc.fileHandle, doc.s3_key].map((k) => (k || "").trim()).filter(Boolean);
  if (!keys.length) {
    const url = (doc.url || doc.downloadUrl || "").trim();
    if (url) keys.push(url.split("?")[0]);
  }
  return [...new Set(keys)];
}

/** A raw document ref in the viewer shape ``getShapePdf`` returns, or null. */
function viewerPdf(doc, filename) {
  const handle = (doc.handle || doc.fileHandle || doc.s3_key || "").trim();
  const url = (doc.url || doc.downloadUrl || "").trim();
  if (!handle && !url) return null;
  return {
    storage: doc.storage,
    handle: handle || null,
    url: url || null,
    filename: (doc.filename || doc.file_name || filename).trim(),
  };
}

/** Where the API reads each document kind (``coverage_comparison_service._PDF_FIELD_GROUPS``). */
const PDF_FIELD_GROUPS = [
  ["quote", "quotePdf", "quotePdfs", "quote_pdfs", "shapeQuotePackage"],
  ["policy", "policyPdf", "policyPdfs", "policy_pdfs", "shapePolicyPackage"],
  ["binder", "binderPdf", "binderPdfs", "binder_pdfs", "shapeBinderPackage"],
];

const asList = (value) => (Array.isArray(value) ? value : value ? [value] : []);

/**
 * Every PDF on the quote's detail once, tagged with viewer kind (S2CWB-225): the top
 * level and the SHAPE package, singular and plural fields, and ``attachments`` - every
 * place the API takes a document from. Citations use findShapePdfByKey; getShapePdf
 * only picks the default of a kind.
 */
export function shapePdfEntries(quote) {
  const detail = quote?.detail;
  if (!detail || typeof detail !== "object") return [];
  const candidates = [];
  for (const [kind, singular, plural, snake, packageKey] of PDF_FIELD_GROUPS) {
    const containers = [detail];
    if (detail[packageKey] && typeof detail[packageKey] === "object") containers.push(detail[packageKey]);
    for (const container of containers) {
      for (const doc of [...asList(container[plural]), ...asList(container[snake]), container[singular]]) {
        candidates.push([kind, doc]);
      }
    }
  }
  for (const doc of asList(detail.attachments)) candidates.push(["quote", doc]);

  const entries = [];
  for (const [kind, doc] of candidates) {
    if (!doc || typeof doc !== "object") continue;
    const pdf = viewerPdf(doc, SHAPE_PDF[kind]?.filename || "binder.pdf");
    if (!pdf) continue;
    const keys = docKeys(doc);
    if (entries.some((e) => keys.some((k) => e.keys.includes(k)))) continue;
    entries.push({ kind, keys, pdf });
  }
  return entries;
}

/**
 * The quote's document whose ``handle`` / ``fileHandle`` / ``s3_key`` is exactly
 * ``documentKey``, as ``{ kind, pdf }``, or null. Never a different document.
 */
export function findShapePdfByKey(quote, documentKey) {
  const key = (documentKey || "").trim();
  if (!key) return null;
  const entry = shapePdfEntries(quote).find((e) => e.keys.includes(key));
  return entry ? { kind: entry.kind, pdf: entry.pdf } : null;
}

/** @param {"quote"|"policy"} kind */
export function hasShapePdf(quote, kind) {
  if (kind === "policy" && policyPdfsFromDetail(quote?.detail).length) {
    return true;
  }
  if (kind === "quote" && quotePdfsFromDetail(quote?.detail).length) {
    return true;
  }
  const pdf = getShapePdf(quote, kind);
  return Boolean((pdf?.handle || pdf?.url || "").trim());
}

/** @param {"quote"|"policy"} kind */
export function shapePdfLabel(kind) {
  if (kind === "binder") return "Binder";
  return SHAPE_PDF[kind]?.label || "Document";
}

/** @param {"quote"|"policy"} kind */
export function shapePdfLoadErrorMessage(kind) {
  if (kind === "policy") {
    return "Unable to load policy document. Please try again.";
  }
  return "Unable to load quote document. Please try again.";
}

/** True when a SigV2-style S3 presigned ``Expires`` epoch is in the past. */
export function isPresignedUrlExpired(url) {
  const match = (url || "").match(/(?:[?&]Expires=)(\d+)/);
  if (!match) return false;
  return Math.floor(Date.now() / 1000) >= Number(match[1]);
}

/**
 * Shape compare-quotes iframe source. The URL is used exactly as given: a presigned S3
 * URL is signed over its query string (Signature V4), so adding or reordering a query
 * parameter invalidates it. Viewer options - and ``page`` (1-based) for a coverage
 * citation - go only in the fragment, which is never sent to the server.
 */
export function pdfViewerIframeSrc(url, { zoom = "Fit", page = null } = {}) {
  const full = (url || "").trim();
  if (!full) return "";
  const hashAt = full.indexOf("#");
  const base = hashAt === -1 ? full : full.slice(0, hashAt);
  const view = zoom === "Fit" || zoom === "FitH" ? "zoom=Fit" : `zoom=${zoom}`;
  const pageNum = page === null || page === undefined || page === "" ? null : Number(page);
  const pageFrag = pageNum != null && Number.isInteger(pageNum) && pageNum > 0 ? `page=${pageNum}&` : "";
  return `${base}#${pageFrag}toolbar=0&navpanes=0&${view}`;
}

/** View-only file object for PdfViewer header + iframe (S3 needs presigned ``url``). */
export function shapePdfChosenFile(pdf, fileName) {
  const handle = (pdf?.handle || pdf?.s3_key || "").trim();
  if (!handle && !(pdf?.url || "").trim()) return null;
  const name = (fileName || pdf?.filename || "Document").trim();
  const url = (pdf?.url || "").trim();

  if (isS3PdfRef(pdf)) {
    return url ? { url, file_name: name } : null;
  }

  return { url: url || `${FILESTACK_CDN}${handle}`, file_name: name };
}

function findBindPackageQuote(quotes, displayStatus) {
  const list = Array.isArray(quotes) ? quotes : [];
  const status = String(displayStatus || "").toLowerCase();

  if (status === "in force") {
    return list.find((q) => q.rowStatus === "Bound") || null;
  }

  if (status === "binding") {
    return (
      list.find((q) => q.bindInProgress) ||
      list.find((q) => q.rowStatus === "BindRequested") ||
      list.find((q) => q.rowStatus === "Bound") ||
      null
    );
  }

  return null;
}

function toDocRow(row, fallbackName) {
  if (!row || typeof row !== "object") return null;
  const handle = (row.handle || row.fileHandle || "").trim();
  const url = (row.url || row.downloadUrl || "").trim();
  const s3_key = (row.s3_key || "").trim();
  if (!handle && !url && !s3_key) return null;
  const name = (
    row.label ||
    row.name ||
    row.title ||
    row.filename ||
    row.file_name ||
    fallbackName ||
    "Document"
  ).trim();
  const filename = (row.filename || row.file_name || name).trim();
  const signingUrl = (row.signingUrl || row.signing_url || "").trim() || null;
  const ref = { storage: row.storage, s3_key, handle, url };

  if (isS3PdfRef(ref)) {
    return {
      name,
      filename,
      handle: null,
      downloadUrl: url || null,
      storage: row.storage || "s3",
      s3_key: s3_key || (handle.includes("/") ? handle : null),
      signingUrl,
    };
  }

  return {
    name,
    filename,
    handle: handle || null,
    downloadUrl: url || null,
    signingUrl,
  };
}

function signingDocRow(row) {
  if (!row || typeof row !== "object") return null;
  const signingUrl = (row.signing_url || row.signingUrl || "").trim();
  if (!signingUrl) return null;
  const name = (row.document_name || row.documentName || row.name || "Document").trim();
  return { name, signingUrl };
}

function normalizeDocName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\.pdf$/i, "");
}

function resolveSigningMatch(docKey, signingMeta, consumed) {
  if (!docKey || consumed.has(docKey) || !signingMeta.has(docKey)) return null;
  consumed.add(docKey);
  return signingMeta.get(docKey);
}

function signingEntriesFromDetail(detail) {
  if (!detail || typeof detail !== "object") return [];
  const signing = detail.signing;
  return Array.isArray(signing) ? signing : [];
}

function signingMetaByDocName(detail) {
  const map = new Map();
  for (const row of signingEntriesFromDetail(detail)) {
    if (!row || typeof row !== "object") continue;
    const url = (row.signing_url || row.signingUrl || "").trim();
    const label = (row.document_name || row.documentName || row.name || "").trim();
    const key = normalizeDocName(label);
    if (url && key) map.set(key, { url, label: label || key });
  }
  return map;
}

function attachSigningUrls(docs, signingMeta) {
  if (!signingMeta.size) return docs;
  const consumed = new Set();
  return docs.map((doc) => {
    if ((doc.signingUrl || "").trim()) return doc;
    const key = normalizeDocName(doc.filename || doc.name);
    const match = resolveSigningMatch(key, signingMeta, consumed);
    if (!match) return doc;
    return {
      ...doc,
      signingUrl: match.url,
      name: match.label || doc.name,
    };
  });
}

function unmatchedSigningDocs(detail, docs) {
  const out = [];
  for (const row of signingEntriesFromDetail(detail)) {
    const doc = signingDocRow(row);
    if (!doc) continue;
    const key = normalizeDocName(doc.name);
    if (!key) continue;
    const matched = docs.some((rowDoc) => {
      if (!(rowDoc.signingUrl || "").trim()) return false;
      return normalizeDocName(rowDoc.filename || rowDoc.name) === key;
    });
    if (matched) continue;
    out.push(doc);
  }
  return out;
}

function addDocs(out, list) {
  if (!list) return;
  const rows = Array.isArray(list) ? list : [list];
  for (const row of rows) {
    const doc = toDocRow(row);
    if (doc) out.push(doc);
  }
}

function collectBindPackageDocuments(quote) {
  const out = [];
  const detail = quote?.detail;
  const quotePdfs = quotePdfsFromDetail(detail);
  const binderPdfs = binderPdfsFromDetail(detail);
  const policyPdfs = policyPdfsFromDetail(detail);

  if (quotePdfs.length) {
    addDocs(out, quotePdfs);
  } else {
    const quotePdf = toDocRow(getShapePdf(quote, "quote"), "Quote");
    if (quotePdf) out.push(quotePdf);
  }

  if (binderPdfs.length) {
    addDocs(out, binderPdfs);
  }

  if (policyPdfs.length) {
    addDocs(out, policyPdfs);
  } else {
    const policyPdf = toDocRow(getShapePdf(quote, "policy"), SHAPE_PDF.policy.label);
    if (policyPdf) out.push(policyPdf);
  }

  if (detail && typeof detail === "object") {
    addDocs(out, detail.documents);
    addDocs(out, detail.policy_forms || detail.policyForms);
  }

  const signingMeta = signingMetaByDocName(detail);
  let docs = attachSigningUrls(out, signingMeta);
  for (const doc of unmatchedSigningDocs(detail, docs)) docs.push(doc);

  const seen = new Set();
  return docs.filter((doc) => {
    const norm = normalizeDocName(doc.filename || doc.name);
    const key = norm || doc.signingUrl || doc.s3_key || doc.handle || doc.downloadUrl || doc.name;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function parseBindPackageDetail(apiResponse) {
  const s = apiResponse?.submission || {};
  const firstOption = Array.isArray(s.options) && s.options.length ? s.options[0] : {};
  return {
    insuranceType: s.insuranceType,
    effectiveDate: firstOption.effectiveDate,
    coverageLimit: s.limit,
    targetPremium: firstOption.targetPremium,
  };
}

/** Filestack handle or S3 key for bind-package panel download. */
export function bindPackageDocDownloadFile(doc) {
  if (!doc || typeof doc !== "object") return null;
  const name = (doc.filename || doc.name || "download").trim();
  const s3_key = (doc.s3_key || "").trim();
  if (s3_key) return { s3_key, file_name: name, filename: name };
  const handle = (doc.handle || "").trim();
  if (handle) return { handle, file_name: name, filename: name };
  const url = (doc.downloadUrl || "").trim();
  if (url) return { url, file_name: name, filename: name };
  return null;
}

export function bindPackageViewFromSubmission({ submission, parsedDetail, quotes, companyName }) {
  const displayStatus = formatSubmissionStatusFromRow(submission);
  const quote = findBindPackageQuote(quotes, displayStatus);
  const detail = quote?.detail || {};

  return {
    status: displayStatus,
    companyName: companyName || "",
    insuranceType: parsedDetail?.insuranceType || submission?.type || "",
    year: submission?.year || "",
    carrier: (quote?.carrierName || "").trim(),
    premium: quote?.premiumAmount ?? parsedDetail?.targetPremium ?? null,
    effectiveDate: parsedDetail?.effectiveDate ?? detail.effectiveDate ?? null,
    coverageLimit: quote?.coverageLimitAmount ?? parsedDetail?.coverageLimit ?? null,
    documents: quote ? collectBindPackageDocuments(quote) : [],
  };
}
